import React, { useEffect, useRef, useState } from 'react';
import {
  CockpitUser,
  requestLoginCode,
  saveProfile,
  verifyLoginCode,
} from '../services/auth';
import { IDENTITY_PEOPLE, personLabel } from '../context/IdentityContext';

/**
 * Sign in to the cockpit.
 *
 * Three steps, and the third only ever runs once per person:
 *   1. Email. It has to be on the access roster; we mail a 6-digit code.
 *   2. Code. Proves they own the address, and hands back a session token.
 *   3. Name. First login only: who they are. That name becomes their identity across
 *      the whole app (the "You are" selector in the navbar starts from it).
 *
 * This replaced a shared 3-word password that was checked in the browser. It went
 * when Revenue Challenge applicants started using the cockpit alongside the team:
 * a password everyone shares can't be taken back from one person who leaves.
 */

interface Props {
  onSignedIn: (user: CockpitUser) => void;
}

type Step = 'email' | 'code' | 'name';

const Login: React.FC<Props> = ({ onSignedIn }) => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [repSlug, setRepSlug] = useState('');
  const [user, setUser] = useState<CockpitUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const codeInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
    if (step === 'name') nameInputRef.current?.focus();
  }, [step]);

  const describeError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    return fallback;
  };

  const submitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);
    try {
      const result = await requestLoginCode(email.trim());
      if (!result.sent) {
        setError(result.message || "That address can't sign in right now.");
        return;
      }
      setNotice(`Code sent to ${email.trim()}. It expires in ${result.expires_in_minutes || 10} minutes.`);
      setStep('code');
    } catch (e) {
      setError(describeError(e, "Couldn't reach the server. Try again in a moment."));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result = await verifyLoginCode(email.trim(), code.trim());
      setUser(result.user);
      if (result.needs_profile) {
        setNotice('');
        setStep('name');
        return;
      }
      onSignedIn(result.user);
    } catch (e) {
      setError(describeError(e, 'That code is wrong or has expired.'));
    } finally {
      setBusy(false);
    }
  };

  const submitName = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const updated = await saveProfile(name.trim(), repSlug || null);
      onSignedIn(updated);
    } catch (e) {
      setError(describeError(e, "Couldn't save your name. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError('');
    setBusy(true);
    try {
      const result = await requestLoginCode(email.trim());
      setNotice(result.sent ? 'New code sent.' : result.message || '');
      if (!result.sent) setError(result.message || '');
    } catch (e) {
      setError(describeError(e, "Couldn't send another code."));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '16px',
    backgroundColor: '#fff',
    color: '#111827',
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: busy ? '#93c5fd' : '#3b82f6',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: busy ? 'default' : 'pointer',
    marginTop: '16px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  };

  const linkButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px 0 0 0',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f9fafb',
        padding: '16px',
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxWidth: '440px',
          width: '100%',
        }}
      >
        <h1 style={{ margin: '0 0 8px 0', textAlign: 'center', color: '#111827', fontSize: '22px' }}>
          AI Command Center
        </h1>
        <p style={{ margin: '0 0 28px 0', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
          {step === 'email' && 'Sign in with the email you were given access with.'}
          {step === 'code' && 'Enter the 6-digit code we just emailed you.'}
          {step === 'name' && 'One last thing: who are you?'}
        </p>

        {step === 'email' && (
          <form onSubmit={submitEmail}>
            <label style={labelStyle} htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={inputStyle}
              required
            />
            <button type="submit" style={buttonStyle} disabled={busy}>
              {busy ? 'Sending...' : 'Send me a code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode}>
            <label style={labelStyle} htmlFor="login-code">
              Sign-in code
            </label>
            <input
              id="login-code"
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              style={{ ...inputStyle, letterSpacing: '8px', fontSize: '22px', textAlign: 'center' }}
              required
            />
            <button type="submit" style={buttonStyle} disabled={busy || code.length < 6}>
              {busy ? 'Checking...' : 'Sign in'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" style={linkButtonStyle} onClick={resend} disabled={busy}>
                Send another code
              </button>
              <button
                type="button"
                style={linkButtonStyle}
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                  setNotice('');
                }}
              >
                Use a different email
              </button>
            </div>
          </form>
        )}

        {step === 'name' && (
          <form onSubmit={submitName}>
            <label style={labelStyle} htmlFor="login-name">
              Your name
            </label>
            <input
              id="login-name"
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ada Lovelace"
              style={inputStyle}
              required
            />
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '8px 0 0 0' }}>
              This is how your work is signed across the app: tasks you create, chat messages,
              uploads, approvals. You can change it later from the name selector in the header.
            </p>

            <div style={{ marginTop: '20px' }}>
              <label style={labelStyle} htmlFor="login-rep">
                Do you have an AI representative here? (optional)
              </label>
              <select
                id="login-rep"
                value={repSlug}
                onChange={(e) => setRepSlug(e.target.value)}
                style={inputStyle}
              >
                <option value="">Not one of the reps</option>
                {IDENTITY_PEOPLE.map((slug) => (
                  <option key={slug} value={slug}>
                    {personLabel(slug)}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '8px 0 0 0' }}>
                Pick your slug only if you already have a command lane. Otherwise leave this alone.
              </p>
            </div>

            <button type="submit" style={buttonStyle} disabled={busy || !name.trim()}>
              {busy ? 'Saving...' : 'Finish'}
            </button>
          </form>
        )}

        {notice && !error && (
          <div
            style={{
              marginTop: '20px',
              padding: '12px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              color: '#1d4ed8',
              fontSize: '13px',
              textAlign: 'center',
            }}
          >
            {notice}
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: '20px',
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '13px',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {user && step === 'name' && (
          <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', marginTop: '16px' }}>
            Signed in as {user.email}
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
